import { initializeApp } from 'firebase/app'
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  FacebookAuthProvider,
  RecaptchaVerifier,
  signInWithPhoneNumber
} from 'firebase/auth'
import { getFirestore, connectFirestoreEmulator, enableIndexedDbPersistence } from 'firebase/firestore'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
}

// Validate critical Firebase config
if (!firebaseConfig.apiKey) {
  console.error('Firebase API key is missing - check environment variables')
}

// Initialize Firebase
export const app = initializeApp(firebaseConfig)

// Initialize Firebase services
export const auth = getAuth(app)
export const db = getFirestore(app, 'wild-draw')
export const storage = getStorage(app)
export const functions = getFunctions(app, 'europe-west1')

// CRITICAL FIX: Enable offline persistence for Firestore
// This allows users to access raffle data even when offline
// Must be called after getFirestore but before any other Firestore operations
enableIndexedDbPersistence(db).catch((err) => {
  if (err.code === 'failed-precondition') {
    // Multiple tabs open - offline persistence limited to one tab
    console.warn('[Firebase] Offline persistence unavailable: Multiple tabs open')
  } else if (err.code === 'unimplemented') {
    // Browser doesn't support IndexedDB persistence
    console.warn('[Firebase] Offline persistence unavailable: Browser not supported')
  } else {
    console.error('[Firebase] Error enabling offline persistence:', err)
  }
})

// Connect to emulators in development
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true })
    connectFirestoreEmulator(db, 'localhost', 8080)
    connectStorageEmulator(storage, 'localhost', 9199)
    connectFunctionsEmulator(functions, 'localhost', 5001)
    // Firebase Emulators connected successfully
  } catch (error) {
    // Firebase Emulators not available, using production
  }
}

// Auth providers
export const googleProvider = new GoogleAuthProvider()
googleProvider.addScope('profile')
googleProvider.addScope('email')

export const facebookProvider = new FacebookAuthProvider()
facebookProvider.addScope('email')

// reCAPTCHA v2 for Firebase compatibility (Enterprise validation done separately)
export const createRecaptchaVerifier = (containerId: string) => {
  // Validate container exists
  const container = document.getElementById(containerId)
  if (!container) {
    throw new Error(`reCAPTCHA container with id '${containerId}' not found`)
  }

  // Clear any existing content
  container.innerHTML = ''

  return new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: (_response: string) => {
      // reCAPTCHA verified successfully
    },
    'expired-callback': () => {
      // reCAPTCHA expired - user needs to solve it again
    },
    'error-callback': (error: any) => {
      console.error('reCAPTCHA verification error:', error)
    }
  })
}

export { signInWithPhoneNumber, RecaptchaVerifier }
export default app